import { logger } from 'lib/logger';
import { equals } from 'ramda';
import { BillingDataAPI } from 'reactApp/api/billing/data/BillingDataAPICall';
import type { BillingData, BillingDataResponse } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendXray } from 'reactApp/utils/ga';
import { pingRequest } from 'reactApp/utils/ping';
import { captureException } from 'reactApp/utils/tracer';
import { call, cancel, put, select, takeEvery } from 'redux-saga/effects';

import {
    billingDataFailure,
    getBillingDataRequest,
    getBillingDataRequestIfNotLoaded,
    getBillingDataSuccess,
    updateBillingDataRequest,
    updateBillingDataSuccess,
} from './billingData.module';
import { loadBillingDataIfNotLoaded } from './billingData.provider';
import { BillingDataSelectors } from './billingData.selectors';
import { BillingDataNotifications } from './billingData.types';

const pickFields = (from: BillingData, fields?: (keyof BillingData)[]): Partial<BillingData> => {
    if (!fields) {
        return from;
    }
    const picked = {};
    for (const field of fields) {
        if (from[field]) {
            picked[field] = from[field];
        }
    }
    return picked;
};

const checkBillingDataEquals = (oldData: Partial<BillingData>, trackFields?: (keyof BillingData)[]) => (data: BillingDataResponse) => {
    const resp = data || {};
    const newData = pickFields(resp.data || {}, trackFields);

    return !equals(newData, oldData);
};

function* handleBillingDataRequest(action: ReturnType<typeof getBillingDataRequest>) {
    const isUserWithBilling = yield select(UserSelectors.isUserWithBilling);
    if (!isUserWithBilling) {
        yield cancel();
    }
    const { radar } = action.payload;
    try {
        sendXray(['billingData', radar, 'request']);
        const data: BillingDataResponse = yield call(BillingDataAPI.current, true, true);
        if (!data) {
            sendXray(['billingData', radar, 'request', 'empty', 'error']);
            logger.error(`no billing data (${radar}) from api`);
            captureException(`no billing (${radar}) data from api`, { issueKey: `billingData/${radar}/empty` });
            yield cancel();
        }
        sendXray(['billingData', radar, 'request', 'success']);
        yield put(getBillingDataSuccess({ billingData: data }));
    } catch (error) {
        sendXray(['billingData', radar, 'request', 'error']);
        logger.error(error);
        captureException(error, { issueKey: `billingData/${radar}/error` });
        yield put(billingDataFailure());
        yield cancel();
    }
}

function* handleUpdateBillingDataRequest(action: ReturnType<typeof updateBillingDataRequest>) {
    const { showSnackbar = false, radar, trackFields = ['android', 'web', 'ios'] } = action.payload || {};

    const isUserWithBilling = yield select(UserSelectors.isUserWithBilling);
    if (!isUserWithBilling) {
        yield cancel();
    }

    sendXray(['billingData', radar, 'update', 'request']);
    try {
        const { data }: BillingDataResponse = yield select(BillingDataSelectors.getBillindDataRaw);

        const initial = pickFields(data, trackFields);

        const res: BillingDataResponse = yield pingRequest({
            request: () => BillingDataAPI.current(false, false),
            count: action.payload.count,
            check: checkBillingDataEquals(initial, trackFields),
        });

        sendXray(['billingData', radar, 'update', 'request', 'success']);
        yield put(updateBillingDataSuccess({ billingData: res }));

        if (showSnackbar) {
            yield put(
                showSnackbarAction({
                    text: 'Список подписок обновлён',
                    type: SnackbarTypes.success,
                    id: BillingDataNotifications.updateSuccess,
                    closable: true,
                })
            );
        }
    } catch (error) {
        sendXray(['billingData', radar, 'update', 'request', 'error']);
        logger.error(error);
        captureException(error, { issueKey: `billingData/${radar}/update/error` });
        yield put(billingDataFailure());
        showSnackbarAction({
            text: 'Не удалось обновить список подписок',
            type: SnackbarTypes.failure,
            id: BillingDataNotifications.updateFailure,
            closable: true,
        });

        yield cancel();
    }
}

export function* watchBillingDataRoot() {
    yield takeEvery(getBillingDataRequestIfNotLoaded.toString(), loadBillingDataIfNotLoaded);
    yield takeEvery(getBillingDataRequest.toString(), handleBillingDataRequest);
    yield takeEvery(updateBillingDataRequest.toString(), handleUpdateBillingDataRequest);
}
